import { chunkItems } from '@/utils/spCampaignCreateDraft';
import { parseSbBidRecommendations } from '@/utils/sbAdGroupCreate';

/** Amazon bid recommendation APIs accept at most 100 keywords per request. */
export const WORKSPACE_SUGGESTED_BID_CHUNK = 100;

export type WorkspaceSuggestedBid = {
  suggestedBid: number | null;
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
};

export type WorkspaceSuggestedBidKeyword = {
  keywordId: string;
  keywordText: string;
  matchType: string;
  adProduct: string;
  campaignId: string;
  adGroupId: string;
  rowKind?: 'keyword' | 'auto_target';
};

type SpKeywordExpressionType = 'KEYWORD_EXACT_MATCH' | 'KEYWORD_BROAD_MATCH' | 'KEYWORD_PHRASE_MATCH';

export type SpSuggestedBidRequest = {
  keywordIds: string[];
  body: {
    recommendationType: 'BIDS_FOR_EXISTING_AD_GROUP';
    campaignId: string;
    adGroupId: string;
    targetingExpressions: Array<{ type: SpKeywordExpressionType; value: string }>;
  };
};

export type SbSuggestedBidRequest = {
  keywordIds: string[];
  body: {
    campaignId: string;
    keywords: Array<{ keywordText: string; matchType: 'broad' | 'phrase' | 'exact' }>;
  };
};

const SP_EXPRESSION_BY_MATCH: Record<string, SpKeywordExpressionType> = {
  EXACT: 'KEYWORD_EXACT_MATCH',
  BROAD: 'KEYWORD_BROAD_MATCH',
  PHRASE: 'KEYWORD_PHRASE_MATCH',
};

function normalizedMatch(matchType: string): 'EXACT' | 'BROAD' | 'PHRASE' | null {
  const upper = matchType.trim().toUpperCase();
  return upper === 'EXACT' || upper === 'BROAD' || upper === 'PHRASE' ? upper : null;
}

function bidLookupKey(keywordText: string, matchType: string): string {
  return `${keywordText.trim().toLowerCase()}\0${matchType.trim().toUpperCase()}`;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Groups SP keywords by ad group and SB keywords by campaign, chunked to the API limit. */
export function buildWorkspaceSuggestedBidRequests(rows: WorkspaceSuggestedBidKeyword[]): {
  sp: SpSuggestedBidRequest[];
  sb: SbSuggestedBidRequest[];
} {
  const spGroups = new Map<string, WorkspaceSuggestedBidKeyword[]>();
  const sbGroups = new Map<string, WorkspaceSuggestedBidKeyword[]>();
  for (const row of rows) {
    if (row.rowKind === 'auto_target' || !row.keywordText.trim() || !normalizedMatch(row.matchType)) continue;
    if (!row.campaignId) continue;
    const product = row.adProduct.trim().toUpperCase();
    if (product === 'SP') {
      if (!row.adGroupId) continue;
      const key = `${row.campaignId}\0${row.adGroupId}`;
      spGroups.set(key, [...(spGroups.get(key) ?? []), row]);
    } else if (product === 'SB') {
      sbGroups.set(row.campaignId, [...(sbGroups.get(row.campaignId) ?? []), row]);
    }
  }

  const sp: SpSuggestedBidRequest[] = [];
  for (const group of spGroups.values()) {
    for (const chunk of chunkItems(group, WORKSPACE_SUGGESTED_BID_CHUNK)) {
      sp.push({
        keywordIds: chunk.map((row) => row.keywordId),
        body: {
          recommendationType: 'BIDS_FOR_EXISTING_AD_GROUP',
          campaignId: chunk[0].campaignId,
          adGroupId: chunk[0].adGroupId,
          targetingExpressions: chunk.map((row) => ({
            type: SP_EXPRESSION_BY_MATCH[normalizedMatch(row.matchType) as string],
            value: row.keywordText,
          })),
        },
      });
    }
  }

  const sb: SbSuggestedBidRequest[] = [];
  for (const [campaignId, group] of sbGroups) {
    for (const chunk of chunkItems(group, WORKSPACE_SUGGESTED_BID_CHUNK)) {
      sb.push({
        keywordIds: chunk.map((row) => row.keywordId),
        body: {
          campaignId,
          keywords: chunk.map((row) => ({
            keywordText: row.keywordText,
            matchType: (normalizedMatch(row.matchType) as string).toLowerCase() as 'broad' | 'phrase' | 'exact',
          })),
        },
      });
    }
  }
  return { sp, sb };
}

/**
 * Suggested bids keyed by keyword text + match type from an SP
 * `/sp/targets/bid/recommendations` response. Prefers the conversion theme;
 * `bidValues` is [low, suggested, high] (a single value is the suggestion).
 */
export function parseSpTargetBidRecommendations(data: unknown): Map<string, WorkspaceSuggestedBid> {
  const out = new Map<string, WorkspaceSuggestedBid>();
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const themes = Array.isArray(root.bidRecommendations) ? root.bidRecommendations : [];
  const themeRecords = themes.filter(
    (theme): theme is Record<string, unknown> => Boolean(theme) && typeof theme === 'object'
  );
  const ordered = [
    ...themeRecords.filter((theme) => theme.theme === 'CONVERSION_OPPORTUNITIES'),
    ...themeRecords.filter((theme) => theme.theme !== 'CONVERSION_OPPORTUNITIES'),
  ];
  for (const theme of ordered) {
    const recs = theme.bidRecommendationsForTargetingExpressions;
    if (!Array.isArray(recs)) continue;
    for (const rec of recs) {
      if (!rec || typeof rec !== 'object') continue;
      const item = rec as Record<string, unknown>;
      const expr =
        item.targetingExpression && typeof item.targetingExpression === 'object'
          ? (item.targetingExpression as Record<string, unknown>)
          : {};
      const type = typeof expr.type === 'string' ? expr.type : '';
      const value = typeof expr.value === 'string' ? expr.value : '';
      const matchType = Object.keys(SP_EXPRESSION_BY_MATCH).find((m) => SP_EXPRESSION_BY_MATCH[m] === type);
      if (!matchType || !value.trim()) continue;
      const key = bidLookupKey(value, matchType);
      if (out.has(key)) continue;
      const values = (Array.isArray(item.bidValues) ? item.bidValues : []).map((v) =>
        v && typeof v === 'object' ? finiteNumber((v as Record<string, unknown>).suggestedBid) : null
      );
      out.set(
        key,
        values.length >= 3
          ? { suggestedBidMin: values[0], suggestedBid: values[1], suggestedBidMax: values[2] }
          : { suggestedBid: values[0] ?? null, suggestedBidMin: null, suggestedBidMax: null }
      );
    }
  }
  return out;
}

export function parseSbSuggestedBids(data: unknown): Map<string, WorkspaceSuggestedBid> {
  const out = new Map<string, WorkspaceSuggestedBid>();
  for (const row of parseSbBidRecommendations(data)) {
    out.set(bidLookupKey(row.keywordText, row.matchType), {
      suggestedBid: row.suggestedBid,
      suggestedBidMin: row.suggestedBidMin,
      suggestedBidMax: row.suggestedBidMax,
    });
  }
  return out;
}

/** Maps parsed text + match type bids back onto the keyword ids of one request. */
export function assignSuggestedBidsToKeywords(
  rows: WorkspaceSuggestedBidKeyword[],
  keywordIds: string[],
  bids: Map<string, WorkspaceSuggestedBid>,
  into: Map<string, WorkspaceSuggestedBid>
): void {
  const ids = new Set(keywordIds);
  for (const row of rows) {
    if (!ids.has(row.keywordId)) continue;
    const bid = bids.get(bidLookupKey(row.keywordText, row.matchType));
    if (bid) into.set(row.keywordId, bid);
  }
}
